import React, { useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Button, Chip, Stack, Typography } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { fetchPacingHolds } from '@src/api/actions';

const TIER_LABEL = {
  slow_down: 'Slow Down',
  minimal_spend: 'Minimal Spend',
  boost: 'Boost',
};

const TIER_COLOR = {
  slow_down: 'warning',
  minimal_spend: 'error',
  boost: 'success',
};

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Surfaces any active pacing adjustments for a client so they can see at a glance
 * that the optimizer is being shaped by their requests. Renders nothing when no
 * holds are active. Drop into any client-facing page that takes a `clientId`.
 */
export default function ActivePacingHoldsBanner({ clientId }) {
  const [holds, setHolds] = useState([]);

  useEffect(() => {
    if (!clientId) {
      setHolds([]);
      return;
    }
    fetchPacingHolds({ client_id: clientId, active: 'true' })
      .then((res) => {
        const rows = res.data?.results || res.data || [];
        setHolds(rows);
      })
      .catch(() => setHolds([]));
  }, [clientId]);

  if (!holds.length) return null;

  return (
    <Alert
      severity="info"
      icon={<TuneIcon fontSize="inherit" />}
      sx={{
        borderRadius: 2,
        mb: 2,
        '& .MuiAlert-message': { width: '100%' },
      }}
      action={
        <Button
          color="inherit"
          size="small"
          href={`/clients/${clientId}/pacing-control`}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Manage
        </Button>
      }
    >
      <AlertTitle sx={{ fontWeight: 700, mb: 0.5 }}>
        {holds.length === 1
          ? 'Pacing adjustment in effect'
          : `${holds.length} pacing adjustments in effect`}
      </AlertTitle>
      <Stack spacing={0.75}>
        {(() => {
          // Collapse batched holds into one line ("3 territories" instead of
          // listing each row). Singletons render as-is.
          const groups = [];
          const byBatch = new Map();
          for (const h of holds) {
            if (!h.batch_id) { groups.push({ lead: h, members: [h] }); continue; }
            if (!byBatch.has(h.batch_id)) {
              const g = { lead: h, members: [] };
              byBatch.set(h.batch_id, g);
              groups.push(g);
            }
            byBatch.get(h.batch_id).members.push(h);
          }
          const visible = groups.slice(0, 4);
          return (
            <>
              {visible.map((g, i) => {
                const h = g.lead;
                const isBatch = g.members.length > 1;
                return (
                  <Stack key={isBatch ? `b-${h.batch_id}` : `s-${h.id}`} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      label={TIER_LABEL[h.tier] || h.tier_label || h.tier}
                      color={TIER_COLOR[h.tier] || 'default'}
                      variant="outlined"
                      sx={{ height: 20 }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {isBatch
                        ? `${g.members.length} territories`
                        : (h.scope_label || h.scope_type_label)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      through {formatDate(h.end_date)}
                    </Typography>
                  </Stack>
                );
              })}
              {groups.length > 4 && (
                <Typography variant="caption" color="text.secondary">
                  …and {groups.length - 4} more.
                </Typography>
              )}
            </>
          );
        })()}
      </Stack>
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          The optimizer is shifting allocation accordingly. Adjustments auto-expire at month-end.
        </Typography>
      </Box>
    </Alert>
  );
}
